/**
 * Diagnostics: what the last few sits actually did.
 *
 * This screen exists because a session with five audible defects produced no evidence at
 * all. The recording is always on — see `src/audio/trace.ts` for why — and this is where
 * it becomes readable, and copyable into a bug report.
 *
 * It is deliberately not hidden behind a gesture or a build flag. Someone who has just
 * heard a gap needs to be able to find it while they still remember which minute it was
 * in, and a diagnostic that takes seven taps to reach is a diagnostic nobody sends.
 *
 * It also reads dumps back in. For its whole life this screen could write a file nothing
 * could open — including itself. That is the wrong half to have: the sit fails on a
 * phone, and the person who can read a trace is at a desk with the phone's dump pasted
 * into a chat window. Opening one here shows it through the same renderer as a local
 * sit, so what the reader sees is what the device recorded and not a second rendering of
 * it that could disagree.
 */

import { useEffect, useState } from 'preact/hooks';
import { anomalyTotals, parseExport, trace } from '../audio/trace';
import type { AnomalyKind, SessionTrace, TraceEntry, TraceExport } from '../audio/trace';
import { previewTrace } from '../session/preview-trace';
import { broadcastDiagnostics } from '../diagnostics';
import { clock } from './clock';
import { entryDuty } from './diagnostic-clock';
import {
  exportArchive, listArchivedSessions, readArchivedSession,
  clearSessionArchive, type ArchivedSessionSummary,
} from '../state/session-archive';

interface Props { onBack: () => void }

/** What each anomaly means, in the words of someone who has to decide whether it matters. */
const EXPLAIN: Record<AnomalyKind, string> = {
  late_schedule: 'audio was handed to the speaker after the moment it was due',
  horizon_underrun: 'assembly fell close to the playhead',
  fill_reentry: 'two assembly passes overlapped',
  decode_mismatch: 'a decoded file was a different length than expected',
  buffer_short: 'a piece of audio was shorter than the session asked for',
  ctx_state: 'the device suspended or resumed audio',
  visibility: 'the app was backgrounded or brought forward',
  page_lifecycle: 'the browser froze, restored, navigated, or discarded the page',
  clock_drift: 'the audio clock and the wall clock disagreed',
  play_retry: 'the playback service said nothing was loaded, just after loading it',
  stalled: 'playback ran out of assembled audio',
  qa_fail: 'a quality check on synthesised silence failed',
  error: 'something threw',
};

function when(ms: number): string {
  const d = new Date(ms);
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function Debug({ onBack }: Props) {
  // Read once per render rather than subscribing: the trace is written from the audio
  // path and a signal there would put UI work on the wrong side of a sixty-minute sit.
  const [tick, setTick] = useState(0);
  const [imported, setImported] = useState<TraceExport & { skipped: number } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [pasting, setPasting] = useState(false);
  const live = trace.snapshot();
  const sessions = imported ? imported.sessions : trace.sessions();
  const [open, setOpen] = useState<string | null>(sessions[0]?.id ?? null);
  const [anomaliesOnly, setAnomaliesOnly] = useState(true);
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);
  const [sendFailed, setSendFailed] = useState(false);
  const [archived, setArchived] = useState<ArchivedSessionSummary[] | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [archiveExcluded, setArchiveExcluded] = useState(0);
  const [archiveOpen, setArchiveOpen] = useState<string | null>(null);
  const [archiveCopied, setArchiveCopied] = useState(false);

  useEffect(() => {
    void listArchivedSessions().then((result) => {
      setArchived(result.sessions); setArchiveExcluded(result.excludedCorruptRows);
    }, (error: Error) => {
      setArchived(null);
      setArchiveError(`Local session archive is unavailable: ${error.message}`);
    });
  }, []);

  // One entry point for both routes in. Opening a dump selects its newest sit, because
  // the reason anyone opens one is to look at a sit that has already gone wrong.
  const load = (text: string) => {
    try {
      const parsed = parseExport(text);
      setImported(parsed);
      setImportError(null);
      setPasting(false);
      setOpen(parsed.sessions[0]?.id ?? null);
    } catch (e) {
      setImported(null);
      setImportError((e as Error).message);
    }
  };

  const totals = anomalyTotals(sessions);
  const totalCount = Object.values(totals).reduce((a, n) => a + (n ?? 0), 0);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(trace.exportJson());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard denial is not fatal; the explicit send remains available */ }
  };

  const send = async () => {
    setSending(true);
    setSendResult(null);
    const result = await broadcastDiagnostics(trace.exportJson());
    setSending(false);
    if (result.stored) {
      setSendFailed(false);
      setSendResult(`Sent · ${result.key}`);
    } else {
      setSendFailed(true);
      setSendResult(`Could not send: ${result.reason}`);
    }
  };

  const clear = () => {
    trace.clear();
    previewTrace.clear();
    void clearSessionArchive().then(() => {
      setArchived([]); setArchiveExcluded(0); setArchiveError(null);
    });
    setTick(tick + 1);
  };

  const copyArchive = async () => {
    try {
      await navigator.clipboard.writeText(await exportArchive());
      setArchiveCopied(true);
      setTimeout(() => setArchiveCopied(false), 2000);
    } catch (error) { setArchiveError(`Could not export archive: ${(error as Error).message}`); }
  };

  return (
    <div class="screen selectable">
      <div class="shell stack" style={{ flex: 1 }}>
        <button class="btn btn-quiet" onClick={onBack}
                style={{ alignSelf: 'flex-start', minHeight: '2.5rem', paddingLeft: 0 }}>
          ← Back
        </button>

        <h1>Diagnostics</h1>
        <p class="dim" style={{ fontSize: 'var(--step--1)' }}>
          A record of what the last few sits did — every piece of audio scheduled, and
          anything that did not go as planned. Each sit is stored on this device and
          uploaded automatically unless Upload session traces is turned off in Settings.
          Sending below broadcasts every sit this device currently holds.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--gap-1)' }}>
          <button class="btn" onClick={copy}>{copied ? 'Copied' : 'Copy diagnostics'}</button>
          <button class="btn" disabled={sending} onClick={() => void send()}>
            {sending ? 'Sending…' : 'Send diagnostics'}
          </button>
          {/* Delete is hidden while a dump is open: it wipes THIS device, and offering it
              beside somebody else's sits is one mistap from destroying the wrong trace. */}
          {!imported && <button class="btn btn-quiet" onClick={clear}>Delete</button>}
        </div>

        {sendResult && (
          <p role="status" style={{ color: sendFailed ? 'var(--warn)' : 'inherit',
                                    fontSize: 'var(--step--1)', margin: 0,
                                    overflowWrap: 'anywhere' }}>
            {sendResult}
          </p>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--gap-1)',
                      alignItems: 'center' }}>
          <label class="btn btn-quiet" style={{ cursor: 'pointer' }}>
            Open a saved file
            <input
              type="file"
              accept="application/json,.json"
              style={{ display: 'none' }}
              onChange={(e) => {
                const f = (e.currentTarget as HTMLInputElement).files?.[0];
                // Reset the input, or choosing the same file twice after an edit is a no-op.
                (e.currentTarget as HTMLInputElement).value = '';
                if (f) void f.text().then(load, (err: Error) => setImportError(err.message));
              }}
            />
          </label>
          <button class="btn btn-quiet" onClick={() => { setPasting(!pasting); setImportError(null); }}>
            {pasting ? 'Cancel' : 'Paste a dump'}
          </button>
          {imported && (
            <button class="btn btn-quiet"
                    onClick={() => { setImported(null); setImportError(null);
                                     setOpen(trace.sessions()[0]?.id ?? null); }}>
              Back to this device
            </button>
          )}
        </div>

        {pasting && (
          <textarea
            class="selectable"
            rows={4}
            autoFocus
            placeholder="Paste the diagnostics JSON here"
            style={{ width: '100%', fontFamily: 'inherit', fontSize: 'var(--step--1)' }}
            onPaste={(e) => {
              const text = e.clipboardData?.getData('text');
              if (text) { e.preventDefault(); load(text); }
            }}
            onChange={(e) => load((e.currentTarget as HTMLTextAreaElement).value)}
          />
        )}

        {importError && (
          <p role="alert" style={{ color: 'var(--warn)', fontSize: 'var(--step--1)' }}>
            Could not read that: {importError}
          </p>
        )}

        {imported && (
          <section class="stack-sm">
            <h3 class="eyebrow">Opened from a file</h3>
            <p class="dim fig" style={{ fontSize: 'var(--step--1)', margin: 0 }}>
              {imported.sessions.length} sit{imported.sessions.length === 1 ? '' : 's'}
              {imported.exportedAt
                ? ` · exported ${new Date(imported.exportedAt).toLocaleString()}`
                : ''}
              {imported.skipped > 0
                ? ` · ${imported.skipped} record${imported.skipped === 1 ? '' : 's'} skipped`
                : ''}
            </p>
            {/* The device is usually the question. A trace read on a desktop says nothing
                about the phone it came from unless the user agent is on screen. */}
            {imported.userAgent && (
              <p class="dim fig" style={{ fontSize: 'var(--step--2)', margin: 0,
                                          wordBreak: 'break-word' }}>
                {imported.userAgent}
              </p>
            )}
            <p class="dim" style={{ fontSize: 'var(--step--1)', margin: 0 }}>
              Send diagnostics always sends this device's records, never the opened file.
            </p>
          </section>
        )}

        {!imported && (
          <section class="stack-sm" data-testid="session-archive">
            <hr />
            <div class="spread">
              <span>
                <strong>Captured assemblies</strong><br />
                <span class="dim" style={{ fontSize: 'var(--step--1)' }}>
                  Exact session plans kept separately from the three recent live traces.
                </span>
              </span>
              <button class="btn btn-quiet" disabled={!archived?.length}
                      onClick={() => void copyArchive()}>
                {archiveCopied ? 'Archive copied' : 'Copy archive'}
              </button>
            </div>
            {archiveError && <p role="status" class="dim">{archiveError}</p>}
            {archiveExcluded > 0 && <p role="status" class="dim">
              {archiveExcluded} corrupt archive row{archiveExcluded === 1 ? '' : 's'} excluded.
            </p>}
            {archived?.length === 0 && <p class="dim">No durable assemblies captured yet.</p>}
            {archived?.map((item) => {
              const itemKey = `${item.sessionId}\u0000${item.assemblyDigest}`;
              return (
                <div key={itemKey} class="stack-sm">
                  <button class="btn btn-quiet spread"
                          onClick={() => setArchiveOpen(archiveOpen === itemKey ? null : itemKey)}>
                    <span>{item.recordingId}<br /><span class="dim fig">{when(item.savedAt)}</span></span>
                    <span class="fig">{item.hasTrace ? 'assembly + trace' : 'assembly'}</span>
                  </button>
                  {archiveOpen === itemKey && (
                    <div>
                      <p class="dim fig" style={{ overflowWrap: 'anywhere' }}>
                        {item.sessionId}<br />{item.assemblyDigest}
                      </p>
                      <button class="chip" onClick={() => void readArchivedSession(
                        item.sessionId, item.assemblyDigest,
                      ).then((row) => row && navigator.clipboard.writeText(
                        JSON.stringify(row, null, 2),
                      ))}>Copy this captured session</button>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        )}

        {sessions.length === 0 && !imported && (
          <p class="dim">Nothing recorded yet. Play a sit and come back.</p>
        )}

        {totalCount > 0 && (
          <section class="stack-sm">
            <h3 class="eyebrow">Across {sessions.length} sit{sessions.length === 1 ? '' : 's'}</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--gap-1)' }}>
              {Object.entries(totals).map(([k, n]) => (
                <span key={k} class="chip" title={EXPLAIN[k as AnomalyKind]}>
                  {k.replace(/_/g, ' ')} · {n}
                </span>
              ))}
            </div>
          </section>
        )}

        {sessions.map((s) => (
          <SessionCard
            key={s.id}
            session={s}
            live={!imported && live?.id === s.id}
            open={open === s.id}
            anomaliesOnly={anomaliesOnly}
            onToggleFilter={() => setAnomaliesOnly(!anomaliesOnly)}
            onToggle={() => setOpen(open === s.id ? null : s.id)}
          />
        ))}
      </div>
    </div>
  );
}

function SessionCard({ session, live, open, anomaliesOnly, onToggle, onToggleFilter }: {
  session: SessionTrace;
  live: boolean;
  open: boolean;
  anomaliesOnly: boolean;
  onToggle: () => void;
  onToggleFilter: () => void;
}) {
  const h = session.header as Record<string, unknown>;
  const count = Object.values(session.anomalies).reduce((a, n) => a + (n ?? 0), 0);
  const rows = session.entries.map((entry, index) => ({
    entry,
    previous: index > 0 ? session.entries[index - 1] : undefined,
  }));
  const shown = anomaliesOnly
    ? rows.filter(({ entry, previous }) => entry.anomaly || entryDuty(previous, entry) !== null)
    : rows;

  return (
    <section class="stack-sm">
      <hr />
      <button class="btn btn-quiet spread" onClick={onToggle}
              style={{ width: '100%', textAlign: 'left', paddingLeft: 0 }}>
        <span>
          {String(h.title ?? h.recordingId ?? 'session')}
          <br />
          <span class="dim fig" style={{ fontSize: 'var(--step--1)' }}>
            {when(session.startedAt)}
            {live ? ' · playing now' : ''}
            {h.targetSec ? ` · ${Math.round(Number(h.targetSec) / 60)} min` : ''}
            {h.cadence ? ` · ${String(h.cadence)}` : ''}
          </span>
        </span>
        <span class="fig" style={{ color: count ? 'var(--accent-fill)' : 'inherit' }}>
          {count === 0 ? 'clean' : `${count} flagged`}
        </span>
      </button>

      {open && (
        <>
          <dl class="dim fig" style={{ fontSize: 'var(--step--1)', display: 'grid',
                gridTemplateColumns: 'auto 1fr', columnGap: 'var(--gap-1)', margin: 0 }}>
            {Object.entries(h).map(([k, v]) => (
              <>
                <dt key={`k${k}`} style={{ opacity: 0.7 }}>{k}</dt>
                <dd key={`v${k}`} style={{ margin: 0 }}>{format(v)}</dd>
              </>
            ))}
          </dl>

          {session.dropped > 0 && (
            <p class="dim" style={{ fontSize: 'var(--step--1)' }}>
              {session.dropped} entries were dropped to stay within the size limit — that
              many events is itself unusual.
            </p>
          )}

          <div class="spread">
            <span class="dim" style={{ fontSize: 'var(--step--1)' }}>
              {shown.length} of {session.entries.length} events
            </span>
            <button class="chip" aria-pressed={anomaliesOnly} onClick={onToggleFilter}>
              {anomaliesOnly ? 'Findings only' : 'Everything'}
            </button>
          </div>

          <div style={{ maxHeight: '20rem', overflowY: 'auto' }}>
            {shown.map(({ entry, previous }) => (
              <Entry key={entry.seq} entry={entry} previous={previous} />
            ))}
            {shown.length === 0 && (
              <p class="dim" style={{ fontSize: 'var(--step--1)' }}>
                No anomalies or clock gaps were found in this sit.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function Entry({ entry, previous }: { entry: TraceEntry; previous: TraceEntry | undefined }) {
  const duty = entryDuty(previous, entry);
  return (
    <div class="fig" style={{ fontSize: 'var(--step--1)', display: 'grid',
          gridTemplateColumns: '4.5rem 1fr', columnGap: 'var(--gap-1)',
          padding: '0.15rem 0' }}>
      <span class="dim">{clock(entry.ctxSec)}</span>
      <span>
        <span style={{ color: entry.anomaly ? 'var(--accent-fill)' : 'inherit' }}>
          {entry.kind.replace(/_/g, ' ')}
        </span>
        {entry.anomaly && EXPLAIN[entry.kind as AnomalyKind] && (
          <span class="dim"> — {EXPLAIN[entry.kind as AnomalyKind]}</span>
        )}
        {entry.detail && (
          <span class="dim"> {Object.entries(entry.detail)
            .map(([k, v]) => `${k}=${formatDetail(k, v)}`).join(' ')}</span>
        )}
        {duty && (
          <span class="dim" data-testid="entry-duty">
            {' '}· +{duty.wallSec}s wall · +{duty.audioSec}s audio · {duty.dutyPct}% duty
          </span>
        )}
      </span>
    </div>
  );
}

function formatDetail(key: string, value: unknown): string {
  return key === 'dutyPct' && typeof value === 'number' ? `${value}%` : format(value);
}

function format(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(3);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
